/**
 * First-touch acquisition attribution.
 *
 * `captureFirstTouchAttribution()` runs on the landing site AND the app on
 * every page load. On a visitor's very first touch it derives where they came
 * from (utm params → ad click ids → referrer → direct) plus the landing page
 * they arrived on, and persists it in a first-party `fs_attr` cookie on the
 * eTLD+1 (`.formsuite.co`) so it survives the cross-subdomain hop from
 * formsuite.co to app.formsuite.co. Subsequent page loads never overwrite it —
 * this is deliberately first-touch attribution.
 *
 * At signup the cookie is read (client-side for the email flow, server-side
 * via `cookies()` for the Google OAuth flow) and stamped onto
 * `User.signupSource / signupMedium / signupCampaign / signupLandingPage /
 * signupReferrer`, which flow into PostHog person properties via
 * `syncUserToPostHog`.
 */

export const ATTRIBUTION_COOKIE_NAME = 'fs_attr';
const ATTRIBUTION_COOKIE_MAX_AGE_DAYS = 90;

export interface FirstTouchAttribution {
  /** e.g. "google", "producthunt.com", "direct" */
  source: string;
  /** e.g. "cpc", "organic", "referral", "none" */
  medium: string;
  campaign?: string;
  term?: string;
  content?: string;
  /** Full document.referrer of the first touch (external referrers only). */
  referrer?: string;
  /** Pathname of the first page viewed, e.g. "/" or "/typeform-alternative". */
  landingPage?: string;
  /** Host of the first page viewed, e.g. "formsuite.co". */
  landingHost?: string;
  /** ISO timestamp of the first touch. */
  ts?: string;
}

const SEARCH_ENGINE_HOSTS = [
  'google.',
  'bing.',
  'duckduckgo.',
  'search.yahoo.',
  'ecosia.',
  'search.brave.',
  'startpage.',
  'baidu.',
  'yandex.',
];

/** Ad click ids that imply a paid source when utm params are missing. */
const CLICK_ID_SOURCES: Array<{ param: string; source: string; medium: string }> = [
  { param: 'gclid', source: 'google', medium: 'cpc' },
  { param: 'msclkid', source: 'bing', medium: 'cpc' },
  { param: 'fbclid', source: 'facebook', medium: 'paid-social' },
  { param: 'ttclid', source: 'tiktok', medium: 'paid-social' },
  { param: 'twclid', source: 'twitter', medium: 'paid-social' },
];

function baseDomain(hostname: string): string | null {
  if (!hostname || hostname === 'localhost' || /^[\d.]+$/.test(hostname)) return null;
  const parts = hostname.split('.');
  if (parts.length < 2) return null;
  return parts.slice(-2).join('.');
}

function isInternalReferrer(referrerHost: string, currentHost: string): boolean {
  const refBase = baseDomain(referrerHost);
  const curBase = baseDomain(currentHost);
  return Boolean(refBase && curBase && refBase === curBase);
}

export function getStoredAttribution(): FirstTouchAttribution | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${ATTRIBUTION_COOKIE_NAME}=([^;]*)`)
  );
  if (!match?.[1]) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(match[1])) as FirstTouchAttribution;
    if (!parsed || typeof parsed.source !== 'string') return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeAttributionCookie(value: FirstTouchAttribution): void {
  const encoded = encodeURIComponent(JSON.stringify(value));
  const maxAge = ATTRIBUTION_COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
  const base = baseDomain(window.location.hostname);
  const suffix = `; path=/; max-age=${maxAge}; SameSite=Lax${
    window.location.protocol === 'https:' ? '; Secure' : ''
  }`;

  if (base) {
    document.cookie = `${ATTRIBUTION_COOKIE_NAME}=${encoded}; domain=.${base}${suffix}`;
  }
  // If the domain-scoped write was rejected (public-suffix hosts like
  // *.vercel.app) fall back to a host-only cookie so at least same-host
  // signups keep attribution.
  if (!getStoredAttribution()) {
    document.cookie = `${ATTRIBUTION_COOKIE_NAME}=${encoded}${suffix}`;
  }
}

function deriveAttribution(): FirstTouchAttribution {
  const url = new URL(window.location.href);
  const params = url.searchParams;
  const referrer = document.referrer || '';

  const utmSource = params.get('utm_source')?.trim();
  const utmMedium = params.get('utm_medium')?.trim();
  const utmCampaign = params.get('utm_campaign')?.trim();
  const utmTerm = params.get('utm_term')?.trim();
  const utmContent = params.get('utm_content')?.trim();

  let source = utmSource || '';
  let medium = utmMedium || '';

  if (!source) {
    const clickId = CLICK_ID_SOURCES.find(({ param }) => params.get(param));
    if (clickId) {
      source = clickId.source;
      medium = medium || clickId.medium;
    }
  }

  let externalReferrer: string | undefined;
  if (referrer) {
    try {
      const refHost = new URL(referrer).hostname;
      if (!isInternalReferrer(refHost, url.hostname)) {
        externalReferrer = referrer;
        if (!source) {
          source = refHost.replace(/^www\./, '');
          medium =
            medium ||
            (SEARCH_ENGINE_HOSTS.some((h) => refHost.includes(h)) ? 'organic' : 'referral');
        }
      }
    } catch {
      // unparseable referrer — ignore
    }
  }

  if (!source) {
    source = 'direct';
    medium = medium || 'none';
  }

  return {
    source: source.slice(0, 255),
    medium: (medium || 'none').slice(0, 64),
    campaign: utmCampaign?.slice(0, 255) || undefined,
    term: utmTerm?.slice(0, 255) || undefined,
    content: utmContent?.slice(0, 255) || undefined,
    referrer: externalReferrer?.slice(0, 500),
    landingPage: url.pathname.slice(0, 500),
    landingHost: url.hostname,
    ts: new Date().toISOString(),
  };
}

/**
 * Capture first-touch attribution for the current visitor. Idempotent — a
 * stored attribution is never overwritten, so calling this on every page
 * load (landing app bootstrap + app useTrack init) is safe.
 */
export function captureFirstTouchAttribution(): FirstTouchAttribution | null {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;
  const existing = getStoredAttribution();
  if (existing) return existing;
  try {
    const attribution = deriveAttribution();
    writeAttributionCookie(attribution);
    return attribution;
  } catch {
    return null;
  }
}
